import { Card } from '../ui/card';
import { cn } from '../../lib/utils';
import { 
  FileSpreadsheet, 
  Users,
  AlertCircle,
  RefreshCw,
  Phone,
  UserCheck,
  UserX
} from 'lucide-react';
import { Button } from '../ui/button';

export const ExternalServicesSection = ({ externalServices, onRefresh, className }) => {
  const smartsheet = externalServices?.smartsheet;
  const ringcentral = externalServices?.ringcentral;
  const hasSmartsheetData = smartsheet && !smartsheet.error && smartsheet.data_source === 'live';
  // RingCentral is valid if it has live data AND user_count > 0 (to detect permission issues)
  const hasRingCentralData = ringcentral && !ringcentral.error && ringcentral.data_source === 'live' && ringcentral.user_count > 0;
  const isRingCentralPermissionIssue = ringcentral?.error_code === 'PERMISSION_DENIED' || ringcentral?.error_code === 'NO_DATA';
  
  return (
    <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="external-services-section">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>
          External Services
        </h3>
        {onRefresh && (
          <Button variant="ghost" size="sm" onClick={onRefresh} data-testid="refresh-external-services">
            <RefreshCw className="w-4 h-4" />
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* RingCentral Card */}
        <Card className="p-6 bg-muted/30 border-border/50" data-testid="ringcentral-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-[#FF6A00]/10">
              <Phone className="w-6 h-6 text-[#FF6A00]" />
            </div>
            <div>
              <h4 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>RingCentral</h4>
              <p className="text-xs text-muted-foreground">{ringcentral?.service_plan || 'Phone System'}</p>
            </div>
          </div>
          
          {hasRingCentralData ? (
            <div className="space-y-4">
              {/* User Count - Large Display */}
              <div className="text-center p-4 rounded-lg bg-muted/20">
                <p className="text-xs text-muted-foreground mb-1">Total Users</p>
                <p className="text-3xl font-bold font-mono text-primary" data-testid="rc-user-count">
                  {ringcentral.user_count}
                </p>
              </div>
              
              {/* User Status Breakdown */}
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center p-2 rounded bg-muted/10">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <UserCheck className="w-3 h-3 text-[rgb(var(--success))]" />
                    <span className="text-xs text-muted-foreground">Enabled</span>
                  </div>
                  <p className="font-mono font-medium text-[rgb(var(--success))]">
                    {ringcentral.users_by_status?.Enabled || 0}
                  </p>
                </div>
                <div className="text-center p-2 rounded bg-muted/10">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <UserX className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Disabled</span>
                  </div>
                  <p className="font-mono font-medium text-muted-foreground">
                    {ringcentral.users_by_status?.Disabled || 0}
                  </p>
                </div>
              </div>
              
              {/* Extensions Summary */}
              {ringcentral.total_extensions > 0 && (
                <div className="pt-3 border-t border-border/50">
                  <div className="text-sm text-muted-foreground">
                    <span>{ringcentral.total_extensions} total extensions</span>
                    {ringcentral.extensions_by_type && Object.keys(ringcentral.extensions_by_type).length > 0 && (
                      <div className="mt-1 text-xs">
                        {Object.entries(ringcentral.extensions_by_type).map(([type, count]) => 
                          <span key={type} className="mr-2">{count} {type}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-[rgb(var(--destructive))] opacity-50" />
              <p className="text-sm text-[rgb(var(--destructive))]">Err</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-[200px] mx-auto">
                {isRingCentralPermissionIssue ? (
                  <>
                    <span className="block font-medium text-[rgb(var(--warning))]">Permission Issue</span>
                    <span>Add ReadAccounts &amp; EditExtensions scopes in RingCentral, then regenerate JWT</span>
                  </>
                ) : (
                  ringcentral?.error || 'Unable to fetch RingCentral data'
                )}
              </p>
            </div>
          )}
        </Card>

        {/* Smartsheet Licenses Card */}
        <Card className="p-6 bg-muted/30 border-border/50" data-testid="smartsheet-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-[#0073ea]/10">
              <FileSpreadsheet className="w-6 h-6 text-[#0073ea]" />
            </div>
            <div>
              <h4 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>Smartsheet</h4>
              <p className="text-xs text-muted-foreground">User Breakdown</p>
            </div>
          </div>
          
          {hasSmartsheetData ? (
            <div className="space-y-4">
              {/* Main Stats - 2x2 Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 rounded-lg bg-muted/20">
                  <p className="text-xs text-muted-foreground mb-1">Members</p>
                  <p className="text-2xl font-bold font-mono text-primary" data-testid="smartsheet-members">
                    {smartsheet.members || smartsheet.licensed_members || 0}
                  </p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/20">
                  <p className="text-xs text-muted-foreground mb-1">Guests</p>
                  <p className="text-2xl font-bold font-mono text-muted-foreground" data-testid="smartsheet-guests">
                    {smartsheet.guests || 0}
                  </p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/20">
                  <p className="text-xs text-muted-foreground mb-1">Viewers</p>
                  <p className="text-2xl font-bold font-mono text-muted-foreground" data-testid="smartsheet-viewers">
                    {smartsheet.viewers || smartsheet.resource_viewers || 0}
                  </p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/20">
                  <p className="text-xs text-muted-foreground mb-1">Provisional</p>
                  <p className="text-2xl font-bold font-mono text-muted-foreground" data-testid="smartsheet-provisional">
                    {smartsheet.provisional_members || 0}
                  </p>
                </div>
              </div>
              
              <div className="pt-3 border-t border-border/50">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total (API)</span>
                  <span className="font-mono font-medium">{smartsheet.total_users}</span>
                </div>
                {smartsheet.note && (
                  <p className="text-xs text-muted-foreground mt-2 italic">{smartsheet.note}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-[rgb(var(--destructive))] opacity-50" />
              <p className="text-sm text-[rgb(var(--destructive))]">Err</p>
              <p className="text-xs text-muted-foreground mt-1">
                {smartsheet?.error || 'Unable to fetch Smartsheet data'}
              </p>
            </div>
          )}
        </Card>
        
        {/* Placeholder for future external services */}
        <Card className="p-6 bg-muted/30 border-border/50 border-dashed opacity-50" data-testid="external-service-placeholder">
          <div className="flex items-center justify-center h-full min-h-[150px]">
            <div className="text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Add more services</p>
            </div>
          </div>
        </Card>
      </div>
    </Card>
  );
};
